import { ScopeButton, SSOLoginButton } from '@monorepo/shared-lib';
import { Row, Typography } from 'antd';
import quickbooksIcon from 'assets/images/quickbooks_icon.svg';
import xeroIcon from 'assets/images/xero_icon.png';
import { SSOImg } from 'components/Custom/images';
import { SSOLoginSpan } from 'components/Custom/spans';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { turnModalOff } from 'store/app/actions';
import { PWA_MOBILE_VIEWPORT_MQ } from 'utils/pwa-nav';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import styled from 'styled-components';

const { Text } = Typography;

const ReviewButtonsContainer = styled.div.attrs({
  className: 'scope-leave-review-modal__marketplace-actions',
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  width: 100%;
  box-sizing: border-box;
`;

const UtilityActions = styled.div.attrs({
  className: 'scope-leave-review-modal__utility-actions',
})`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 20px;
  width: 100%;
  box-sizing: border-box;
`;

const DescriptionText = styled(Text)`
  text-align: center;
  width: 100%;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-dark, #042a2a);
`;

interface LeaveReviewModalProps {
  onAlreadyDid?: () => void;
  onLater?: () => void;
  onNoThanks?: () => void;
}

export const LeaveReviewModal: React.FC<LeaveReviewModalProps> = ({
  onAlreadyDid,
  onLater,
  onNoThanks,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const phoneLayout =
    pwaMobileNav ||
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(PWA_MOBILE_VIEWPORT_MQ).matches);

  const handleAlreadyDid = () => {
    dispatch(turnModalOff());
    if (onAlreadyDid) {
      onAlreadyDid();
    }
  };

  const handleLater = () => {
    dispatch(turnModalOff());
    if (onLater) {
      onLater();
    }
  };

  const handleNoThanks = () => {
    dispatch(turnModalOff());
    if (onNoThanks) {
      onNoThanks();
    }
  };

  const marketplaceButtonWidth = phoneLayout ? '100%' : '280px';
  const utilityButtonWidth = phoneLayout ? '100%' : undefined;

  // Marketplace URLs
  const XERO_MARKETPLACE_URL = 'https://apps.xero.com/app/generatetax';
  const QBO_MARKETPLACE_URL =
    'https://quickbooks.intuit.com/app/apps/appdetails/generatetax/en-global/';

  return (
    <div className="scope-leave-review-modal">
      <Row justify={'center'}>
        <DescriptionText>
          {t('other.leave-review-prompt')}
          <br />
          {t('other.leave-review-subtext')}
        </DescriptionText>
      </Row>

      <ReviewButtonsContainer>
        <SSOLoginButton
          minwidth={marketplaceButtonWidth}
          onClick={() => {
            window.open(XERO_MARKETPLACE_URL, '_blank', 'noopener,noreferrer');
            handleAlreadyDid();
          }}
        >
          <SSOImg src={xeroIcon} />
          <SSOLoginSpan>{t('other.review-on-xero')}</SSOLoginSpan>
        </SSOLoginButton>
        <SSOLoginButton
          minwidth={marketplaceButtonWidth}
          onClick={() => {
            window.open(QBO_MARKETPLACE_URL, '_blank', 'noopener,noreferrer');
            handleAlreadyDid();
          }}
        >
          <SSOImg src={quickbooksIcon} />
          <SSOLoginSpan>{t('other.review-on-quickbooks')}</SSOLoginSpan>
        </SSOLoginButton>
      </ReviewButtonsContainer>

      <UtilityActions>
        <ScopeButton
          type={'default'}
          width={utilityButtonWidth ?? '140px'}
          height={'40px'}
          onClick={handleAlreadyDid}
        >
          {t('other.i-already-did')}
        </ScopeButton>
        <ScopeButton
          type={'default'}
          width={utilityButtonWidth ?? '100px'}
          height={'40px'}
          onClick={handleLater}
        >
          {t('other.later')}
        </ScopeButton>
        <ScopeButton
          type={'default'}
          width={utilityButtonWidth ?? '120px'}
          height={'40px'}
          onClick={handleNoThanks}
          style={{ paddingRight: '16px' }}
        >
          {t('other.no-thanks')}
        </ScopeButton>
      </UtilityActions>
    </div>
  );
};
